/**
 * FreshCards Backdrop — generic dimming-layer primitive.
 *
 * F 99.1: extracted from F 96 design. Sibling to <Modal>. Sits behind
 * any overlay (modal, fullscreen, peek panel, drawer) and dims the
 * underlying content.
 *
 * Why a separate primitive: the dimming layer is reusable across
 * <Modal>, <Fullscreen>, and any future overlay. Keeping it as a
 * primitive lets consumers compose the dim with custom foreground
 * content without rebuilding the dim each time.
 *
 * Behaviour:
 *  - Renders into the document body via createPortal
 *  - Optional click handler (typically closes the overlay)
 *  - Optional opacity override (default 0.45)
 *  - z-index configurable (default 9999)
 *  - Respects prefers-reduced-motion (no animation in that mode)
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface BackdropProps {
  /** Optional click handler (e.g. close the overlay). */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Opacity 0..1. Default 0.45. */
  opacity?: number;
  /** z-index. Default 9999. */
  zIndex?: number;
  /** Optional className for theming. */
  className?: string;
  /**
   * Portal target. Default `document.body`. Pass a node when the backdrop
   * must share a wrapper with sibling content (e.g. <Modal>, where the
   * backdrop and the panel must be siblings under one stacking root).
   */
  container?: HTMLElement | null;
  /** Optional content (most backdrops are empty layers; this is here for special cases). */
  children?: React.ReactNode;
}

export const Backdrop: React.FC<BackdropProps> = ({
  onClick,
  opacity = 0.45,
  zIndex = 9999,
  className,
  container,
  children,
}) => {
  // Skip SSR
  if (typeof document === 'undefined') return null;

  const target = container ?? document.body;

  return createPortal(
    <div
      className={className}
      onClick={onClick}
      data-fc-backdrop=""
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: `rgba(0, 0, 0, ${opacity})`,
        zIndex,
        // Must re-enable hit-testing when portaled into a wrapper that
        // sets pointer-events:none for its own stacking root.
        pointerEvents: 'auto',
        animation: 'fc-backdrop-fade-in 150ms ease-out',
      }}
      role="presentation"
    >
      {children}
    </div>,
    target,
  );
};

export default Backdrop;
